import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { todayDateString, VENUE_TIMEZONE, buildAtlanticDate } from '@/lib/timezone';
export const DatePicker = ({ value, onChange, className = '', id, 'data-testid': dataTestId, disabled = false, variant = 'default', }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempDate, setTempDate] = useState(value || todayDateString());
    const pickerRef = useRef(null);
    const inputRef = useRef(null);
    useEffect(() => {
        if (value) {
            setTempDate(value);
        }
    }, [value]);
    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);
    const handleApply = () => {
        onChange(tempDate);
        setIsOpen(false);
    };
    const handleCancel = () => {
        setTempDate(value || todayDateString());
        setIsOpen(false);
    };
    const formatDisplayDate = (date) => {
        if (!date)
            return 'Select date';
        // Parse YYYY-MM-DD as the venue's calendar day, not the browser's.
        // `new Date(date + 'T00:00:00')` would re-interpret in browser TZ,
        // then formatting with VENUE_TIMEZONE adds the offset again and
        // shifts the displayed day for non-Atlantic admins.
        const [y, m, d] = date.split('-').map(Number);
        const dt = buildAtlanticDate(y, m, d, 12, 0, 0);
        return dt.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: VENUE_TIMEZONE,
        });
    };
    const handleDateChange = (e) => {
        setTempDate(e.target.value);
    };
    const isMissionControl = variant === 'mission-control';
    const triggerClass = isMissionControl
        ? `mc-input flex h-9 w-full cursor-pointer items-center justify-between text-sm disabled:cursor-not-allowed disabled:opacity-50 ${className}`
        : `flex h-9 w-full rounded-md border border-slate-600 bg-slate-900/50 px-3 py-1 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 cursor-pointer items-center justify-between disabled:cursor-not-allowed disabled:opacity-50 ${className}`;
    const popoverClass = isMissionControl
        ? 'mc-popover-content absolute z-50 mt-1 w-72 rounded p-4'
        : 'absolute z-50 mt-1 w-72 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-4';
    const inputClass = isMissionControl
        ? 'mc-input w-full text-sm [color-scheme:dark]'
        : 'w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 [color-scheme:dark]';
    const cancelClass = isMissionControl
        ? 'mc-action-btn mc-action-btn-compact mc-action-btn-fit flex-1'
        : 'flex-1 rounded-md border border-slate-600 px-3 py-2 text-sm text-white hover:bg-slate-700';
    const applyClass = isMissionControl
        ? 'mc-action-btn mc-action-btn-primary mc-action-btn-compact mc-action-btn-fit flex-1'
        : 'flex-1 rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-black hover:bg-amber-600';
    return (_jsxs("div", { className: "relative", ref: pickerRef, children: [_jsxs("button", { type: "button", id: id, "data-testid": dataTestId, disabled: disabled, onClick: () => !disabled && setIsOpen(!isOpen), className: triggerClass, children: [_jsx("span", { children: formatDisplayDate(value) }), _jsx("svg", { className: "w-4 h-4 text-slate-400", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" }) })] }), isOpen && (_jsxs("div", { "data-testid": "date-picker-popover", className: popoverClass, children: [_jsxs("div", { className: "mb-4", children: [_jsx("label", { className: isMissionControl ? 'mc-field-label mb-2 block' : 'block text-xs text-slate-400 mb-2', children: "Select Date" }), _jsx("input", { ref: inputRef, type: "date", value: tempDate, disabled: disabled, onChange: handleDateChange, className: inputClass })] }), _jsx("div", { className: isMissionControl ? 'text-center text-[color:var(--mc-text-primary)] text-base font-semibold mb-4' : 'text-center text-slate-300 text-base font-semibold mb-4', children: formatDisplayDate(tempDate) }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { type: "button", onClick: handleCancel, className: cancelClass, children: "Cancel" }), _jsx("button", { type: "button", onClick: handleApply, className: applyClass, children: "Apply" })] })] }))] }));
};
